import type { Permission } from '@crm/contracts';
import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router';

import { OrganizationGuard } from './OrganizationGuard';

/** A page loaded on demand: the first screen does not carry every section's code. */
function page(path: string, load: () => Promise<ComponentType>): RouteObject {
  return { path, lazy: async () => ({ Component: await load() }) };
}

/** Routes of a section, open to those who may view it (the BFF and gateway check too). */
function section(permission: Permission, routes: RouteObject[]): RouteObject {
  return { element: <OrganizationGuard permission={permission} />, children: routes };
}

/** Pages under ``/o/:organizationId``. */
export const organizationRoutes: RouteObject[] = [
  {
    index: true,
    lazy: async () => ({
      Component: (await import('@/pages/organization')).OrganizationPage,
    }),
  },
  section('equipment.view', [
    page('equipment', async () => (await import('@/pages/equipment')).EquipmentPage),
  ]),
  // Everyone sees their own rentals; rentals.view shows everyone's.
  page('rentals', async () => (await import('@/pages/rentals')).RentalsPage),
  page('rentals/:rentalId', async () => (await import('@/pages/rental')).RentalPage),
  section('telematics.view', [
    page('telematics', async () => (await import('@/pages/telematics')).TelematicsPage),
  ]),
  section('warehouses.view', [
    page('warehouse', async () => (await import('@/pages/warehouses')).WarehousesPage),
  ]),
  section('catalog.view', [
    page('warehouse/catalog', async () => (await import('@/pages/catalog')).CatalogPage),
  ]),
  section('stock.view', [
    page('warehouse/stock', async () => (await import('@/pages/stock')).StockPage),
  ]),
  section('objects.view', [
    page('objects', async () => (await import('@/pages/objects')).ObjectsPage),
    page('objects/:objectId', async () => (await import('@/pages/objects')).ObjectPage),
  ]),
  section('documents.view', [
    page('documents', async () => (await import('@/pages/documents')).DocumentsPage),
  ]),
  {
    element: <OrganizationGuard admin />,
    children: [
      page('employees', async () => (await import('@/pages/employees')).EmployeesPage),
      page('employees/positions', async () => (await import('@/pages/positions')).PositionsPage),
    ],
  },
];
